import { useEffect, useMemo, useState, type RefObject } from 'react'
import type { CanvasHandle, Overview, OverviewNode } from './KnowledgeGraphCanvas'
import { NODE_TYPE_STYLES, SELECTED_NODE_FG } from './nodeIcons'

interface Props {
  canvasRef: RefObject<CanvasHandle | null>
  /** Drop the card chrome; the caller draws one card around this and the zoom row. */
  embedded?: boolean
  /** The node the reader selected on the canvas; drawn as the larger dot in the canvas's selected teal. */
  selectedNodeId?: string | null
}

// The thumbnail as the design draws it (DSGN-63): thin slate edges and small
// dots. Each dot takes its node's colour on the canvas (the type's ring/icon
// colour, the selected node's teal), so the minimap reads like the graph.
// The radii keep the design's dot-to-panel ratios (a dot is ~1.3% of the
// panel width, the selected one ~2%).
const EDGE = '#475569'
const DOT_R = 1.2
const SELECTED_DOT_R = 2
/** A node or link the canvas dims under a filter is faint here too. */
const DIMMED_OPACITY = 0.2

const PANEL_W = 134
const PANEL_H = 119
const PAD = 6

const MinimapPanel = ({ canvasRef, embedded, selectedNodeId = null }: Props) => {
  const [overview, setOverview] = useState<Overview | null>(null)

  useEffect(() => {
    const handle = canvasRef.current
    if (!handle) return
    // The cytoscape layout finishes asynchronously after mount; poll briefly
    // until positions are available, then keep them frozen for the minimap.
    let frame = 0
    const tick = () => {
      const ov = handle.getOverview()
      if (ov && ov.nodes.length > 0) {
        setOverview(ov)
        return
      }
      if (frame++ < 30) requestAnimationFrame(tick)
    }
    tick()
    // Canvases whose nodes move (D3 physics, a layout switch) push updates;
    // the one-shot read above covers the static cytoscape canvas.
    // A plain thumbnail of the graph, as the design draws it: no viewport
    // rectangle (at 100% it covered the whole thumbnail in blue).
    const unsubOverview = handle.subscribeOverview?.(setOverview)
    return () => unsubOverview?.()
  }, [canvasRef])

  const projection = useMemo(() => {
    if (!overview) return null
    const { bbox } = overview
    const gw = Math.max(bbox.x2 - bbox.x1, 1)
    const gh = Math.max(bbox.y2 - bbox.y1, 1)
    const sx = (PANEL_W - PAD * 2) / gw
    const sy = (PANEL_H - PAD * 2) / gh
    const s = Math.min(sx, sy)
    const offsetX = PAD + ((PANEL_W - PAD * 2) - gw * s) / 2
    const offsetY = PAD + ((PANEL_H - PAD * 2) - gh * s) / 2
    const project = (x: number, y: number) => ({
      x: offsetX + (x - bbox.x1) * s,
      y: offsetY + (y - bbox.y1) * s,
    })
    return { project, scale: s, offsetX, offsetY }
  }, [overview])

  const nodeById = useMemo(() => {
    if (!overview) return new Map<string, OverviewNode>()
    return new Map(overview.nodes.map((n) => [n.id, n]))
  }, [overview])

  return (
    <div
      className={
        embedded
          ? 'bg-background dark:bg-dark-card'
          : 'rounded-lg border border-border bg-background shadow-md dark:bg-dark-card'
      }
      style={{ width: PANEL_W, height: PANEL_H }}
      aria-hidden="true"
    >
      <svg width={PANEL_W} height={PANEL_H} className="block">
        {overview && projection && (
          <>
            {overview.edges.map((e, i) => {
              const s = nodeById.get(e.source)
              const t = nodeById.get(e.target)
              if (!s || !t) return null
              const p1 = projection.project(s.x, s.y)
              const p2 = projection.project(t.x, t.y)
              return (
                <line
                  key={i}
                  x1={p1.x}
                  y1={p1.y}
                  x2={p2.x}
                  y2={p2.y}
                  stroke={EDGE}
                  strokeWidth={0.5}
                  opacity={e.dimmed ? DIMMED_OPACITY : undefined}
                />
              )
            })}
            {overview.nodes.map((n) => {
              const p = projection.project(n.x, n.y)
              const selected = n.id === selectedNodeId
              return (
                <circle
                  key={n.id}
                  cx={p.x}
                  cy={p.y}
                  r={selected ? SELECTED_DOT_R : DOT_R}
                  fill={selected ? SELECTED_NODE_FG : NODE_TYPE_STYLES[n.type].fg}
                  opacity={n.dimmed && !selected ? DIMMED_OPACITY : undefined}
                  data-selected={selected ? 'true' : undefined}
                  data-dimmed={n.dimmed ? 'true' : undefined}
                />
              )
            })}
          </>
        )}
      </svg>
    </div>
  )
}

export default MinimapPanel
